import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Star, ShoppingCart, Heart, Filter, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    sortBy: 'newest',
    priceRange: 'all',
  });

  useEffect(() => {
    fetchCategories();
    loadCart();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const loadCart = () => {
    const saved = localStorage.getItem('indicraft_cart');
    if (saved) setCartItems(JSON.parse(saved));
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/products`;
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      
      const response = await axios.get(`${url}?${params.toString()}`);
      let filteredProducts = response.data || [];

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredProducts = filteredProducts.filter(p => 
          p.name.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
        );
      }

      if (filters.priceRange !== 'all') {
        const [min, max] = filters.priceRange.split('-').map(Number);
        filteredProducts = filteredProducts.filter(p => {
          if (max) return p.discount_price >= min && p.discount_price <= max;
          return p.discount_price >= min;
        });
      }

      switch (filters.sortBy) {
        case 'price-low':
          filteredProducts.sort((a, b) => a.discount_price - b.discount_price);
          break;
        case 'price-high':
          filteredProducts.sort((a, b) => b.discount_price - a.discount_price);
          break;
        case 'rating':
          filteredProducts.sort((a, b) => b.rating - a.rating);
          break;
        default:
          break;
      }

      setProducts(filteredProducts);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existing = cartItems.find(item => item.id === product.id);
    let newCart;
    if (existing) {
      newCart = cartItems.map(item => 
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      newCart = [...cartItems, { ...product, quantity: 1 }];
    }
    setCartItems(newCart);
    localStorage.setItem('indicraft_cart', JSON.stringify(newCart));
    toast.success(`${product.name} added to cart!`);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      search: '',
      sortBy: 'newest',
      priceRange: 'all',
    });
    setSearchParams({});
  };

  const selectedCategory = categories.find(c => c.slug === filters.category);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)} />
      
      <main className="flex-grow">
        {/* Hero Banner */}
        <section className="bg-maroon py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              {selectedCategory ? selectedCategory.name : 'Shop All Products'}
            </h1>
            <p className="text-white/70 max-w-2xl mx-auto">
              {selectedCategory?.description || 'Discover our divine collection of authentic puja essentials, handcrafted with devotion for your spiritual journey.'}
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => setFilterOpen(!filterOpen)}
                className="border-maroon/20 text-maroon hover:bg-maroon hover:text-white"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {(filters.category || filters.priceRange !== 'all') && (
                  <span className="ml-2 w-5 h-5 bg-saffron text-white text-xs rounded-full flex items-center justify-center">
                    {[filters.category, filters.priceRange !== 'all'].filter(Boolean).length}
                  </span>
                )}
              </Button>

              {(filters.category || filters.search || filters.priceRange !== 'all') && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-maroon/60 hover:text-maroon"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>

            <div className="flex items-center gap-4">
              <Input
                type="text"
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-48 md:w-64 h-10 rounded-full border-maroon/20"
              />

              <div className="relative">
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  className="h-10 pl-4 pr-10 rounded-full border border-maroon/20 bg-white text-maroon appearance-none cursor-pointer focus:outline-none focus:border-maroon"
                >
                  <option value="newest">Newest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Top Rated</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-maroon/60 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Filters Panel */}
          {filterOpen && (
            <div className="bg-white rounded-2xl p-6 mb-8 border border-gold/10 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Categories */}
                <div>
                  <h3 className="font-heading font-semibold text-maroon mb-3">Categories</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, category: '' }))}
                      className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        !filters.category ? 'bg-maroon text-white' : 'text-maroon hover:bg-cream'
                      }`}
                    >
                      All Categories
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setFilters(prev => ({ ...prev, category: cat.slug }))}
                        className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          filters.category === cat.slug ? 'bg-maroon text-white' : 'text-maroon hover:bg-cream'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <h3 className="font-heading font-semibold text-maroon mb-3">Price Range</h3>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All Prices' },
                      { value: '0-500', label: 'Under ₹500' },
                      { value: '500-1000', label: '₹500 - ₹1000' },
                      { value: '1000-2000', label: '₹1000 - ₹2000' },
                      { value: '2000-', label: 'Above ₹2000' },
                    ].map(range => (
                      <button
                        key={range.value}
                        onClick={() => setFilters(prev => ({ ...prev, priceRange: range.value }))}
                        className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          filters.priceRange === range.value ? 'bg-maroon text-white' : 'text-maroon hover:bg-cream'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Filters */}
                <div>
                  <h3 className="font-heading font-semibold text-maroon mb-3">Quick Filters</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate('/shop?bestseller=true')}
                      className="block w-full text-left px-3 py-2 rounded-lg text-maroon hover:bg-cream transition-colors"
                    >
                      Best Sellers
                    </button>
                    <button
                      onClick={() => navigate('/shop?featured=true')}
                      className="block w-full text-left px-3 py-2 rounded-lg text-maroon hover:bg-cream transition-colors"
                    >
                      Featured Products
                    </button>
                    <button
                      onClick={() => navigate('/shop?collection=festive')}
                      className="block w-full text-left px-3 py-2 rounded-lg text-maroon hover:bg-cream transition-colors"
                    >
                      Festive Collection
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Count */}
          <p className="text-maroon/60 mb-6">
            Showing {products.length} product{products.length !== 1 ? 's' : ''}
            {filters.category && ` in ${selectedCategory?.name || filters.category}`}
          </p>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-cream" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-cream rounded w-3/4" />
                    <div className="h-3 bg-cream rounded w-1/2" />
                    <div className="h-10 bg-cream rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-10 h-10 text-gold/50" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-maroon mb-2">No Products Found</h3>
              <p className="text-maroon/60 mb-6">Try adjusting your filters or search term</p>
              <Button onClick={clearFilters} className="bg-maroon hover:bg-maroon-dark text-white rounded-full">
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-transparent hover:border-gold/30 shadow-sm hover:shadow-[0_8px_30px_rgba(212,175,55,0.15)] transition-all duration-500"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/400'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                      onClick={() => navigate(`/product/${product.id}`)}
                    />
                    
                    {product.discount_percentage > 0 && (
                      <div className="absolute top-3 left-3">
                        <span className="bg-saffron text-white text-xs font-bold px-2 py-1 rounded-full">
                          {product.discount_percentage}% OFF
                        </span>
                      </div>
                    )}
                    
                    {product.is_new && (
                      <div className="absolute top-3 right-3">
                        <span className="bg-maroon text-white text-xs font-bold px-2 py-1 rounded-full">
                          NEW
                        </span>
                      </div>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.success('Added to wishlist!');
                      }}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Heart className="w-4 h-4 text-maroon" />
                    </button>
                  </div>

                  <div className="p-4">
                    <h3 
                      className="font-heading font-semibold text-maroon mb-2 line-clamp-1 cursor-pointer hover:text-maroon-dark"
                      onClick={() => navigate(`/product/${product.id}`)}
                    >
                      {product.name}
                    </h3>

                    <div className="flex items-center gap-1 mb-2">
                      <Star className="w-4 h-4 fill-gold text-gold" />
                      <span className="text-sm font-medium text-maroon">{product.rating}</span>
                      <span className="text-sm text-maroon/60">({product.reviews_count})</span>
                    </div>

                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-xl font-bold text-maroon">
                        ₹{product.discount_price}
                      </span>
                      {product.original_price > product.discount_price && (
                        <span className="text-sm text-maroon/50 line-through">
                          ₹{product.original_price}
                        </span>
                      )}
                    </div>

                    <Button
                      onClick={() => addToCart(product)}
                      className="w-full bg-maroon hover:bg-maroon-dark text-white font-semibold rounded-full py-2 transition-all"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Add to Cart
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ShopPage;
